import React from 'react';
import { trustFeatures } from '../mock';
import { 
  Shield, ExternalLink, CheckCircle, Lock, Key, 
  Database, Wifi, EyeOff, Server, 
  AlertTriangle, ArrowRight
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';

const Safety = () => {
  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName.charAt(0).toUpperCase() + iconName.slice(1)];
    return Icon ? <Icon className="w-8 h-8" /> : null;
  };

  const securityLayers = [
    {
      title: 'DTLS Encryption',
      icon: Lock,
      description: 'All voice data is encrypted with Datagram Transport Layer Security (DTLS) during WebRTC transmission.',
      details: 'TLS 1.3 equivalent for real-time communication. Your voice is encrypted before it leaves your device.',
      color: 'green'
    },
    {
      title: 'JWT Authentication',
      icon: Key,
      description: 'Secure JSON Web Tokens authenticate every API request with short expiration times.',
      details: 'Tokens expire every 15 minutes. Refresh tokens are stored securely. No session hijacking possible.',
      color: 'blue'
    },
    {
      title: 'Row Level Security (RLS)',
      icon: Database,
      description: 'Database policies ensure users can only access their own data. No admin can see your info.',
      details: 'PostgreSQL RLS policies on Supabase. Even with database access, your data is isolated.',
      color: 'purple'
    },
    {
      title: 'Local Execution',
      icon: Server,
      description: 'All automation scripts run locally on your machine. Your files and browser never touch our servers.',
      details: 'Playwright browser control, file operations, and system commands all execute on your hardware only.',
      color: 'orange'
    }
  ];

  const dataFlowItems = [
    { from: 'Your Microphone', to: 'Twe App', encrypted: true, data: 'Voice audio stream' },
    { from: 'Twe App', to: 'OpenAI API', encrypted: true, data: 'Encrypted voice (DTLS)' },
    { from: 'OpenAI API', to: 'Twe App', encrypted: true, data: 'Command intent + params' },
    { from: 'Twe App', to: 'Local Scripts', encrypted: false, data: 'Automation command', local: true },
    { from: 'Local Scripts', to: 'Your System', encrypted: false, data: 'Browser/File/App action', local: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12" data-testid="safety-header">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Shield className="w-6 h-6 text-orange-500" />
            <span className="text-orange-500 font-semibold uppercase tracking-wider text-sm">
              V3.0 Security
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Trust Center
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Complete transparency about how Twe V3.0 protects your privacy and secures your data
          </p>
        </div>

        {/* Hybrid Model Disclosure */}
        <div className="mb-12 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Honest Disclosure: Hybrid Model</h3>
              <p className="text-gray-300 mb-3">
                Twe uses a <strong className="text-orange-400">hybrid local-cloud model</strong>. Here's exactly what goes where:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-black/40 rounded-lg p-4 border border-green-500/30">
                  <h4 className="text-green-400 font-semibold mb-2 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    100% Local (Never leaves your PC)
                  </h4>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Browser control & browsing data</li>
                    <li>• File operations & file content</li>
                    <li>• App launching & window management</li>
                    <li>• System controls (volume, screenshots)</li>
                    <li>• Spotify playback commands</li>
                  </ul>
                </div>
                <div className="bg-black/40 rounded-lg p-4 border border-blue-500/30">
                  <h4 className="text-blue-400 font-semibold mb-2 flex items-center">
                    <Wifi className="w-4 h-4 mr-2" />
                    Cloud (Encrypted, Not Stored)
                  </h4>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Voice audio → OpenAI (real-time, not stored)</li>
                    <li>• Auth tokens → Supabase (RLS protected)</li>
                    <li>• Usage metrics → For billing only</li>
                    <li>• Nothing else. Ever.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Trust Features */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {trustFeatures.map((feature, index) => (
            <div
              key={index}
              className="bg-zinc-900/50 backdrop-blur-sm border border-white/5 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="text-orange-500">
                    {getIcon(feature.icon)}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Security Technologies */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            V3.0 Security Stack
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {securityLayers.map((layer, index) => {
              const colorClasses = {
                green: 'border-green-500/30 bg-green-500/5',
                blue: 'border-blue-500/30 bg-blue-500/5',
                purple: 'border-purple-500/30 bg-purple-500/5',
                orange: 'border-orange-500/30 bg-orange-500/5'
              };
              const iconColors = {
                green: 'text-green-500',
                blue: 'text-blue-500',
                purple: 'text-purple-500',
                orange: 'text-orange-500'
              };
              return (
                <div
                  key={index}
                  className={`bg-zinc-900/50 border rounded-xl p-6 ${colorClasses[layer.color]}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 bg-black/40 rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <layer.icon className={`w-6 h-6 ${iconColors[layer.color]}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">{layer.title}</h3>
                      <p className="text-gray-300 mb-2">{layer.description}</p>
                      <p className="text-gray-500 text-sm">{layer.details}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Data Flow Diagram */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Data Flow Diagram
          </h2>
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-8">
            <div className="space-y-4">
              {dataFlowItems.map((item, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-32 text-right">
                    <span className={`text-sm font-medium ${item.local ? 'text-green-400' : 'text-gray-300'}`}>
                      {item.from}
                    </span>
                  </div>
                  <div className="flex-1 flex items-center">
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-zinc-700 to-zinc-600"></div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium mx-2 flex items-center space-x-1 ${
                      item.encrypted ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                      item.local ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                      'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                    }`}>
                      {item.encrypted && <Lock className="w-3 h-3" />}
                      <span>{item.data}</span>
                    </div>
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-zinc-600 to-zinc-700"></div>
                    <ArrowRight className="w-4 h-4 text-orange-500 mx-2" />
                  </div>
                  <div className="w-32">
                    <span className={`text-sm font-medium ${item.local ? 'text-green-400' : 'text-gray-300'}`}>
                      {item.to}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-400 text-sm">Encrypted</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-400 text-sm">Local Only</span>
              </div>
            </div>
          </div>
        </div>

        {/* Virus Scan Section */}
        <div className="bg-gradient-to-br from-zinc-900 to-black border border-orange-500/30 rounded-xl p-8 mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">
            Independent Security Verification
          </h2>
          <div>
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Shield className="w-5 h-5 text-orange-500 mr-2" />
              VirusTotal Scan
            </h3>
            <p className="text-gray-400 mb-4">
              Every release is automatically scanned by 70+ antivirus engines. Check the latest results yourself:
            </p>
            <a
              href="https://www.virustotal.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="virustotal-link"
              className="inline-flex items-center space-x-2 text-orange-500 hover:text-orange-400 font-medium transition-colors duration-200"
            >
              <span>View VirusTotal Report</span>
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* What We DON'T Collect */}
        <div className="mb-16 bg-zinc-900/50 border border-white/5 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <EyeOff className="w-6 h-6 text-green-500 mr-3" />
            What We DON'T Collect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Browsing history',
              'File contents',
              'Screenshots',
              'Keystrokes',
              'App usage patterns',
              'Location data',
              'Device identifiers',
              'Personal files',
              'Voice recordings (stored)'
            ].map((item, i) => (
              <div key={i} className="flex items-center space-x-2 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Security Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: "Why does Windows SmartScreen show a warning?",
                a: "SmartScreen warnings appear for new software that hasn't reached Microsoft's download threshold (~10,000 users). It's not a virus detection - it's a popularity check. Once enough users download Twe, the warning disappears automatically. We're working on EV code signing to remove this faster."
              },
              {
                q: "Does OpenAI store my voice data?",
                a: "No. OpenAI's Realtime API processes audio in real-time without storing it. Per their API policy, they do not use API data for model training. Your voice is encrypted (DTLS), processed, and discarded."
              },
              {
                q: "Can Twe access my passwords?",
                a: "No. Twe cannot access browser password managers, system keychains, or encrypted credential stores. It can only interact with visible UI elements and execute commands you speak."
              },
              {
                q: "What if I want fully offline mode?",
                a: "We're developing Local LLM support via Ollama (Q2 2025). This will enable fully offline voice processing on your own hardware. Trade-off: slightly lower accuracy vs cloud AI."
              },
              {
                q: "How are JWT tokens secured?",
                a: "Tokens expire every 15 minutes. Refresh tokens are stored in secure OS keychain (Windows Credential Manager). All token transmission uses TLS 1.3. No tokens are ever stored in plain text."
              }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 hover:border-orange-500/30 transition-all duration-300"
              >
                <h3 className="text-lg font-semibold text-white mb-2">{item.q}</h3>
                <p className="text-gray-400 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <p className="text-gray-400 mb-4">
            Found a security issue? We offer bug bounties.
          </p>
          <a
            href="mailto:contact@twe-assistant.com"
            data-testid="security-contact"
            className="text-orange-500 hover:text-orange-400 font-medium transition-colors duration-200"
          >
            contact@twe-assistant.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default Safety;
